// src/index.tsx
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { MantineProvider, ColorSchemeProvider, ColorScheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GroupProvider } from './contexts/GroupContext';
import { routes } from './routes';
import { MsalAuthProvider } from './providers/MsalAuthProvider';
import { MsalErrorBoundary } from './components/error/MsalErrorBoundary';
import { MsalLoader } from './components/auth/MsalLoader';
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create the router
const router = createBrowserRouter(routes);

// Root app component that handles color scheme
const RootApp = () => {
  // Get stored color scheme or default to dark
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    const storedScheme = localStorage.getItem('colorScheme');
    return (storedScheme as ColorScheme) || 'dark';
  });

  const toggleColorScheme = (value?: ColorScheme) => {
    const nextColorScheme = value || (colorScheme === 'dark' ? 'light' : 'dark');
    setColorScheme(nextColorScheme);
    localStorage.setItem('colorScheme', nextColorScheme);
  };

  return (
    <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
      <MantineProvider theme={{ colorScheme }} withGlobalStyles withNormalizeCSS>
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <GroupProvider>
            <MsalErrorBoundary>
              <MsalLoader>
                <MsalAuthProvider>
                  <RouterProvider router={router} />
                </MsalAuthProvider>
              </MsalLoader>
            </MsalErrorBoundary>
          </GroupProvider>
        </QueryClientProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
};

// Add global error handler for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('error', (event) => {
    const message = event?.message ?? '';
    if (
      message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      event.preventDefault();
      return;
    }
    console.error('Global error:', event.error);
  });
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to find the root element');
}

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>
);

